import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';

import AuthScreen from './Pages/AuthScreen';
import TrainingSessionLog from './Pages/TrainingSessionLog';
import HomePage from './Pages/HomePage';
import DashboardPage from './Pages/DashboardPage';
import ProfilePage from './Pages/ProfilePage';
import SettingsPage from './Pages/SettingsPage';
import TrainingHistory from './Pages/TrainingHistory';
import SupportPage from './Pages/SupportPage';
import PrivacyPolicyPage from './Pages/PrivacyPolicyPage';
import VerifyEmailScreen from './Pages/VerifyEmailScreen';
import ForgotPasswordScreen from './Pages/ForgotPasswordScreen';
import ResetPasswordScreen from './Pages/ResetPasswordScreen';
import './App.css';
import NavBar from './components/NavBar';
import { AuthProvider, useAuth } from './context/AuthContext';
import {
  getMuiThemeOptions,
  getThemeCssVariables,
  normalizeTheme,
  THEME_STORAGE_KEY,
} from './theme/appTheme';

export function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search]);

  return null;
}

function AuthedApp({ theme, onThemeChange }) {
  const { currentUser, updateCurrentUser } = useAuth();

  return (
    <>
      <NavBar />
      <Routes>
        <Route path="/" element={<HomePage currentUser={currentUser} />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/log-training" element={<TrainingSessionLog />} />
        <Route
          path="/profile"
          element={(
            <ProfilePage currentUser={currentUser} onUserUpdated={updateCurrentUser} />
          )}
        />
        <Route
          path="/settings"
          element={<SettingsPage theme={theme} onThemeChange={onThemeChange} />}
        />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/training-history" element={<TrainingHistory />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function UnauthedApp() {
  return (
    <Routes>
      <Route path="/" element={<AuthScreen />} />
      <Route path="/verify-email" element={<VerifyEmailScreen />} />
      <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppRoutes({ theme, onThemeChange }) {
  const { isAuthenticated, isAuthLoading } = useAuth();

  if (isAuthLoading) {
    return null;
  }

  return !isAuthenticated ? (
    <UnauthedApp />
  ) : (
    <AuthedApp theme={theme} onThemeChange={onThemeChange} />
  );
}

export default function App() {
  const [theme, setTheme] = useState(() => normalizeTheme(localStorage.getItem(THEME_STORAGE_KEY)));

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const appTheme = useMemo(
    () => createTheme(getMuiThemeOptions(theme)),
    [theme]
  );
  const themeCssVariables = useMemo(
    () => getThemeCssVariables(theme),
    [theme]
  );

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <div
        className="App"
        data-theme={theme}
        style={{ ...themeCssVariables, colorScheme: appTheme.palette.mode }}
      >
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes theme={theme} onThemeChange={setTheme} />
          </BrowserRouter>
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
}
